import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
// TODO update to latest fsd linter to allow cross-imports
// eslint-disable-next-line boundaries/element-types
import type { Filters } from '@/features/filters';
import type { ColumnsConfig, Sorting } from '@/shared/types';
import type { MinMaxData } from '@/shared/ui';
import type { MetricColoring } from '../../model/types';

interface ContextState {
  minMax?: MinMaxData;
  columnsConfig: ColumnsConfig;
  advancedSortableOptions: { name: string; value: string }[];
  sorting: Sorting;
  filters: Filters;
  filtersMap?: Record<string, unknown>;
  filterableIds?: Set<string>;
  renderDimensionFilterPanel?: (id: string, onClose: () => void) => ReactNode;
  renderMetricFilterPanel?: (id: string, onClose: () => void) => ReactNode;
  metricsColoring: MetricColoring[];
  updateSorting: (field: string, sorting?: Sorting[number]) => void;
  updateFilters: (filters: Filters) => void;
  updateMetricsColoring: (metricsColoring: MetricColoring[]) => void;
  onDimensionDelete: (field: string) => void;
  onMetricDelete: (field: string) => void;
}

const Context = createContext<ContextState | null>(null);

interface Props {
  children: ReactNode;
  minMax?: MinMaxData;
  columnsConfig: ColumnsConfig;
  advancedSortableOptions: { name: string; value: string }[];
  sorting: Sorting;
  filters: Filters;
  filtersMap?: Record<string, unknown>;
  filterableIds?: Set<string>;
  renderDimensionFilterPanel?: (id: string, onClose: () => void) => ReactNode;
  renderMetricFilterPanel?: (id: string, onClose: () => void) => ReactNode;
  metricsColoring: MetricColoring[];
  updateMetricsColoring: (metricsColoring: MetricColoring[]) => void;
  updateFilters: (filters: Filters) => void;
  updateSorting: (field: string, sorting?: Sorting[number]) => void;
  onDimensionDelete: (field: string) => void;
  onMetricDelete: (field: string) => void;
}

export function TableHeaderPropsProvider({
  children,
  minMax,
  columnsConfig,
  advancedSortableOptions,
  sorting,
  filters,
  filtersMap,
  filterableIds,
  renderDimensionFilterPanel,
  renderMetricFilterPanel,
  metricsColoring,
  updateMetricsColoring,
  updateFilters,
  updateSorting,
  onDimensionDelete,
  onMetricDelete,
}: Props) {
  return (
    <Context.Provider
      value={{
        minMax,
        columnsConfig,
        advancedSortableOptions,
        sorting,
        filters,
        filtersMap,
        filterableIds,
        renderDimensionFilterPanel,
        renderMetricFilterPanel,
        metricsColoring,
        updateSorting,
        updateFilters,
        updateMetricsColoring,
        onDimensionDelete,
        onMetricDelete,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export const useTableHeaderPropsContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      'useTableHeaderPropsContext hook used outside <TableHeaderPropsProvider />',
    );
  }

  return context;
};
